<template>
  <div
    v-if="hasDefaultSlotContent"
    :class="wrapperClasses"
    data-cy="ds-badge__wrapper"
  >
    <slot />
    <DSBadgeContent v-bind="badgeContentProps" />
  </div>

  <DSBadgeContent
    v-else
    v-bind="badgeContentProps"
  />
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";
import { DSBadgeContentProps, DSBadgeProps } from "@/src/design_system/data_display/badge/types/props";
import { DS_BADGE_STATE } from "@/src/design_system/data_display/badge/types/state";
import { DS_BADGE_POSITION } from "@/src/design_system/data_display/badge/types/position";
import {
  DS_BADGE_CLASSES,
  DS_BADGE_POSITION_TO_CLASS_MAP,
} from "@/src/design_system/data_display/badge/constants/classes";
import { hasSlotContent } from "@/src/shared/utils/slots";
import DSBadgeContent from "@/src/design_system/data_display/badge/components/DSBadgeContent.vue";
import { DS_SIZE } from "@/src/design_system/shared/types/size";

const props = withDefaults(defineProps<DSBadgeProps>(), {
  state: DS_BADGE_STATE.Default,
  size: DS_SIZE.Medium,
  text: undefined,
  icon: undefined,
  disabled: false,
  positionBottom: false,
  ariaLabel: undefined,
});

const slots = useSlots();

const hasDefaultSlotContent = computed(() => {
  return hasSlotContent(slots);
});

const actualPosition = computed(() => {
  if (!hasDefaultSlotContent.value) {
    return DS_BADGE_POSITION.Inline;
  }
  return props.positionBottom ? DS_BADGE_POSITION.BottomRight : DS_BADGE_POSITION.TopRight;
});

const wrapperClasses = computed(() => {
  const classes: string[] = [DS_BADGE_CLASSES.Wrapper];

  const positionClass = DS_BADGE_POSITION_TO_CLASS_MAP[actualPosition.value];
  if (positionClass) {
    classes.push(positionClass);
  }

  return classes;
});

const badgeContentProps = computed(() => {
  return Object.fromEntries(
    Object.entries(props).filter(([key]) => key !== "positionBottom")
  ) as unknown as DSBadgeContentProps;
});
</script>
